import React, { useState, useEffect, useRef } from 'react';
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';

import { Container, Sprite, useApp } from '@pixi/react';
import gsap from "gsap";

import { animateLanding, animateCrash, animateNoFeedback, animateTimeout } from './animations';
import GameButton from "../GameButton/GameButton";

import moonImage from "~/assets/images/moon.png";
import rocketLaunchSite from "~/assets/images/rocket-launch-site-2.png";
import check from "~/assets/images/check.png";
import cross from "~/assets/images/cross.png";
import timeout from "~/assets/images/timeout.png";

const backgroundImage = new Image();
var bgImageWidth, bgImageHeight, bgImageAspectRatio;
backgroundImage.onload = () => {
    bgImageWidth = backgroundImage.width;
    bgImageHeight = backgroundImage.height;
    bgImageAspectRatio = bgImageWidth / bgImageHeight;
};
backgroundImage.src = rocketLaunchSite;


const GameApp = (props) => {
    const { t } = useTranslation(["common", "glossary"]);

    const [buttonsDisabled, setButtonsDisabled] = useState(false);

    const app = useApp();
    
    const sessionId = useRef(props.session.id);
    const playing = useRef(props.playing);
    const timeoutFlag = useRef(props.timeoutFlag);
    const answerTimeCheckpoint = useRef();

    useEffect(() => { window.addEventListener("resize", resizeCanvas); }, []);

    useEffect(() => {
        const backgroundSprite = getSceneItem("background");
        const rocketContainer = getSceneItem("rocket");
        const landButtonContainer = getSceneItem("landButton");
        const crashButtonContainer = getSceneItem("crashButton");
        const checkSprite = getSceneItem("check");
        const crossSprite = getSceneItem("cross");
        const timeoutSprite = getSceneItem("timeout");

        if(props.playing) {
            // A BUTTON
            gsap.fromTo(landButtonContainer, { alpha: 0 }, { alpha: 1, duration: 0.5, ease: "expo.out", delay: 0.5 });
            // B BUTTON
            gsap.fromTo(crashButtonContainer, { alpha: 0 }, { alpha: 1, duration: 0.5, ease: "expo.out", delay: 0.5 });

            // ROCKET
            rocketContainer.alpha = 1;

            rocketContainer.pivot.y = rocketContainer.height * 0.3;

            const rocketAspectRatio = rocketContainer.width / rocketContainer.height;
            rocketContainer.height = backgroundSprite.height * 0.3;
            rocketContainer.width = rocketContainer.height * rocketAspectRatio;
            gsap.fromTo(
                rocketContainer.position,
                { x: window.innerWidth * 0.5, y: window.innerHeight },
                { x: window.innerWidth * 0.5, y: backgroundSprite.position.y + backgroundSprite.height * 0.675, duration: 0.5, ease: "expo.out", delay: 0.5 }
            );


            gsap.fromTo(
                rocketContainer,
                { alpha: 0 },
                {
                    alpha: 1,
                    duration: 0.5,
                    delay: 0.5,
                    ease: "expo.out",
                    onComplete: () => { answerTimeCheckpoint.current = Date.now(); props.updateTimeoutFlag(false); }
                }
            );
            
        } else {
            // A BUTTON
            gsap.fromTo(landButtonContainer, { alpha: 1 }, { alpha: 0, duration: 0.5, ease: "expo.out", delay: 0.5 });
            // B BUTTON
            gsap.fromTo(crashButtonContainer, { alpha: 1 }, { alpha: 0, duration: 0.5, ease: "expo.out", delay: 0.5 });
            // Feedback sprites
            checkSprite.alpha = 0;
            crossSprite.alpha = 0;
            timeoutSprite.alpha = 0;
        }

        playing.current = props.playing;
    }, [props.playing]);

    useEffect(() => {
        gsap.globalTimeline.getChildren().forEach(t => t.kill());

        const moonSprite = getSceneItem("moon");
        const backgroundSprite = getSceneItem("background");
        
        if(props.session.id) {
            // BACKGROUND
            backgroundSprite.alpha = 1;
            gsap.fromTo(
                backgroundSprite.position,
                { x: window.innerWidth < bgImageWidth ? (window.innerWidth - bgImageWidth) * 0.5 : 0, y: window.innerHeight },
                { x: window.innerWidth < bgImageWidth ? (window.innerWidth - bgImageWidth) * 0.5 : 0, y: window.innerHeight - backgroundSprite.height, duration: 0.5, ease: "expo.out" }
            );

            // MOON
            gsap.fromTo(
                moonSprite.position,
                { x: window.innerWidth * 0.5, y: window.innerHeight * 0.75 },
                { x: window.innerWidth * 0.75, y: window.innerHeight * 0.25, duration: 0.5, ease: "expo.out" }
            );
            gsap.fromTo(moonSprite.scale, { x: 1, y: 1 }, { x: 0.75, y: 0.75, duration: 0.5, ease: "expo.out" });

        } else {
            // BACKGROUND
            gsap.to(
                backgroundSprite.position,
                {
                    x: window.innerWidth < bgImageWidth ? (window.innerWidth - bgImageWidth) * 0.5 : 0,
                    y: window.innerHeight,
                    duration: 1,
                    ease: "expo.out",
                    onComplete: () => { backgroundSprite.alpha = 0; }
                },
            );

            // MOON
            gsap.to(
                moonSprite.position,
                { x: window.innerWidth * 0.5, y: window.innerHeight * 0.75, duration: 0.5, ease: "expo.out" },
            );
            gsap.to(moonSprite.scale, { x: 1, y: 1, duration: 0.5, ease: "expo.out" });

            setButtonsDisabled(false);
        }

        sessionId.current = props.session.id;
    }, [props.session.id]);

    useEffect(() => {
        if(timeoutFlag.current != props.timeoutFlag && props.timeoutFlag == true) {
            setButtonsDisabled(true);
    
            const backgroundSprite = getSceneItem("background");
            const rocketContainer = getSceneItem("rocket");
            const timeoutSprite = getSceneItem("timeout");
    
            const animationCallback = () => {
                setButtonsDisabled(false);
                answerTimeCheckpoint.current = Date.now();
                props.updateTimeoutFlag(false);
                props.updateStep();
            };
    
            const answerTime = 5000;
            props.updatePlayerAnswer("timeout", answerTime);

            animateTimeout(
                backgroundSprite,
                rocketContainer,
                timeoutSprite,
                0.5,
                animationCallback
            );
        }

        timeoutFlag.current = props.timeoutFlag;
    }, [props.timeoutFlag]);
    
    const resizeCanvas = () => {
        gsap.globalTimeline.getChildren().forEach(t => t.kill());
        setButtonsDisabled(false);   // WORKAROUND BECAUSE KILLING GSAP ROUTINES CAN ALSO AVOID CALLBACKS THAT RESET BUTTONS

        const moonSprite = getSceneItem("moon");
        const backgroundSprite = getSceneItem("background");

        // BACKGROUND
        backgroundSprite.width = window.innerWidth < bgImageWidth ? bgImageWidth : window.innerWidth;
        backgroundSprite.height = window.innerWidth < bgImageWidth ? bgImageWidth / bgImageAspectRatio : window.innerWidth / bgImageAspectRatio;
        backgroundSprite.position.set(
            (window.innerWidth - backgroundSprite.width) * 0.5,
            sessionId.current ? window.innerHeight - backgroundSprite.height : window.innerHeight
        );

        // MOON
        moonSprite.position.set(
            sessionId.current ? window.innerWidth * 0.75 : window.innerWidth * 0.5,
            sessionId.current ? window.innerHeight * 0.25 : window.innerHeight * 0.75
        );

        if(playing.current) {
            const landButtonContainer = getSceneItem("landButton");
            const crashButtonContainer = getSceneItem("crashButton");
            const rocketContainer = getSceneItem("rocket");
            
            const rocketContainerAspectRatio = rocketContainer.width / rocketContainer.height;

            // "Land" BUTTON
            landButtonContainer.alpha = 1;
            landButtonContainer.position.set(window.innerWidth * 0.25, window.innerHeight * 0.75);
            // "Crash" BUTTON
            crashButtonContainer.alpha = 1;
            crashButtonContainer.position.set(window.innerWidth * 0.75, window.innerHeight * 0.75);

            // ROCKET
            rocketContainer.alpha = 1;
            rocketContainer.rotation = 0;
            rocketContainer.height = backgroundSprite.height * 0.3;
            rocketContainer.width = rocketContainer.height * rocketContainerAspectRatio;
            rocketContainer.position.set(window.innerWidth * 0.5, backgroundSprite.position.y + backgroundSprite.height * 0.675);
        }
    };

    const getRocketImage = (id) => {
        return (
            props.session.patterns[id][0].split("").map((val, i) => {
                return Object.entries(props.images[val])[i][1];
            })
        );
    };

    const getSceneItem = (name) => { return app.stage.children.find(item => item.name == name); };

    const handleClick = (answer) => {
        props.updateTimeoutFlag(true);
        timeoutFlag.current = true;

        setButtonsDisabled(true);
        const answerTime = (Date.now() - answerTimeCheckpoint.current) / 1000;

        const moonSprite = getSceneItem("moon");
        const backgroundSprite = getSceneItem("background");
        const rocketContainer = getSceneItem("rocket");
        const checkSprite = getSceneItem("check");
        const crossSprite = getSceneItem("cross");

        const correctAnswer = props.session.patterns[props.session.step][1];
        const playerAnswer = answer;

        const animationCallback = () => {
            setButtonsDisabled(false);
            answerTimeCheckpoint.current = Date.now();
            props.updateStep();
            props.updateTimeoutFlag(false);
        };

        props.updatePlayerAnswer(playerAnswer, answerTime);

        if(props.session.phase.includes("learning")) {
            if(correctAnswer == 0)
                animateLanding(
                    moonSprite,
                    backgroundSprite,
                    rocketContainer,
                    playerAnswer == correctAnswer ? checkSprite : crossSprite,
                    0.5,
                    animationCallback
                );
            else
                animateCrash(
                    backgroundSprite,
                    rocketContainer,
                    playerAnswer == correctAnswer ? checkSprite : crossSprite,
                    0.5,
                    animationCallback
                );
        } else {
            animateNoFeedback(
                backgroundSprite,
                rocketContainer,
                0.5,
                animationCallback
            );
        }
        
    };

    const rocketImage = props.session.id ? getRocketImage(props.session.step) : null;
    return <>
        <Sprite
            name = 'moon'
            image = { moonImage }
            anchor = { 0.5 }
            x = { sessionId.current ? window.innerWidth * 0.75 : window.innerWidth * 0.5 }
            y = { sessionId.current ? window.innerHeight * 0.25 : window.innerHeight * 0.75 }
        />
        <Sprite
            name = 'background'
            image = { rocketLaunchSite }
            x = { window.innerWidth < bgImageWidth ? (window.innerWidth - bgImageWidth) * 0.5 : 0 }
            y = { window.innerWidth < bgImageWidth ? window.innerHeight - bgImageWidth / bgImageAspectRatio : window.innerHeight - window.innerWidth / bgImageAspectRatio }
            width = { window.innerWidth < bgImageWidth ? bgImageWidth : window.innerWidth }
            height = { window.innerWidth < bgImageWidth ? bgImageWidth / bgImageAspectRatio : window.innerWidth / bgImageAspectRatio }
        />
        <Sprite
            name = 'check'
            image = { check }
            anchor = { 0.5 }
            x = { window.innerWidth * 0.5 }
            y = { window.innerHeight * 0.5 }
            width = { 500 }
            height = { 500 }
            alpha = { 0 }
        />
        <Sprite
            name = 'cross'
            image = { cross }
            anchor = { 0.5 }
            x = { window.innerWidth * 0.5 }
            y = { window.innerHeight * 0.5 }
            width = { 500 }
            height = { 500 }
            alpha = { 0 }
        />
        <Sprite
            name = 'timeout'
            image = { timeout }
            anchor = { 0.5 }
            x = { window.innerWidth * 0.5 }
            y = { window.innerHeight * 0.5 }
            width = { 500 }
            height = { 500 }
            alpha = { 0 }
        />
        {
            props.playing ? 
                <>
                    <GameButton
                        disabled = { buttonsDisabled }
                        name = 'landButton'
                        label = { t("glossary:yes").toUpperCase() }
                        fontSize = { 25 }
                        width = { 150 }
                        height = { 80 }
                        x = { window.innerWidth * 0.25 }
                        y = { window.innerHeight * 0.75 }
                        pivot = {{ x: 75, y: 40 }}
                        onclick = { () => { handleClick(0); } }
                    />
                    <GameButton
                        disabled = { buttonsDisabled }
                        name = 'crashButton'
                        label = { t("glossary:no").toUpperCase() }
                        fontSize = { 25 }
                        width = { 150 }
                        height = { 80 }
                        x = { window.innerWidth * 0.75 }
                        y = { window.innerHeight * 0.75 }
                        pivot = {{ x: 75, y: 40 }}
                        onclick = { () => { handleClick(1); } }
                    />
                    {
                        rocketImage ?
                            <Container name = 'rocket'>
                                <Sprite
                                    name = 'head'
                                    anchor = { [0.5, 0] }
                                    y = { 0 }
                                    height = { rocketImage[0].height }
                                    image = { rocketImage[0].url }
                                >
                                </Sprite>
                                <Sprite
                                    name = 'body'
                                    anchor = { [0.5, 0] }
                                    y = { rocketImage[0].height }
                                    height = { rocketImage[1].height }
                                    image = { rocketImage[1].url }
                                >
                                </Sprite>
                                <Sprite
                                    name = 'propellers'
                                    anchor = { [0.5, 0] }
                                    y = { rocketImage[0].height + rocketImage[1].height }
                                    height = { rocketImage[2].height }
                                    image = { rocketImage[2].url }
                                >
                                </Sprite>
                                <Sprite
                                    name = 'flames'
                                    anchor = { [0.5, 0] }
                                    y = { rocketImage[0].height + rocketImage[1].height + rocketImage[2].height }
                                    height = { rocketImage[3].height }
                                    image = { rocketImage[3].url }
                                >
                                </Sprite>
                            </Container> :
                            null
                    }
                </> :
                null
        }
    </>;
};

GameApp.propTypes = {
    images: PropTypes.arrayOf(PropTypes.object),
    playing: PropTypes.bool,
    session: PropTypes.object,
    updatePlayerAnswer: PropTypes.func,
    updateStep: PropTypes.func,
    timeoutFlag: PropTypes.bool,
    updateTimeoutFlag: PropTypes.func,
};

export default GameApp;